/**
 * Order-ticket logic (#674 / #676 / #677) with no React in it:
 *   - cents input -> price (exact), ticket validation via trading/rules.ts,
 *   - building the signed `order` action (asset id ONLY from hl/outcomes.ts, wire strings ONLY from rules.ts),
 *   - a monotonic per-session nonce,
 *   - sign (wallet, chain 1337 — or the approved agent key in 1-click mode, #688) + POST /exchange, and parsing
 *     the per-order result.
 */
import type { Hex } from 'viem';
import type { EnvConfig } from '../config/env';
import { isAgentMissingError } from '../agent/agent';
import {
  ChainAddError,
  postExchange,
  signL1,
  type Eip1193Provider,
  type ExchangePayload,
  type ExchangeResponse,
} from '../hl/exchange';
import { assetId, OUTCOME_PX_DECIMALS, type Side } from '../hl/outcomes';
import { orderAction, type BuilderInfo, type OrderAction, type Tif } from '../hl/signing';
import { holdSocketsOpen } from '../hl/tabSleep';
import type { L2Book } from '../hl/types';
import { add, cmp, decToString, mul, ONE, parseDec, roundDec, sub, ZERO, type Dec } from '../trading/decimal';
import {
  MIN_ORDER_VALUE_USD,
  OUTCOME_RULES,
  sellsWholeHolding,
  validateOrderIntent,
  validateSize,
  wirePrice,
  wireSize,
  type IntentResult,
  type OutcomeHoldings,
} from '../trading/rules';
import { walletProfile } from '../wallet/compat';
import { isUserRejection, walletErrorMessage } from '../wallet/errors';
import { t, t as tr } from '../i18n/i18n';
import { localizeNumber, usd } from '../i18n/format';

/**
 * What the ticket's "Time in force" selector picks: a Hyperliquid TIF, or the synthetic 'Market' (#746), which is
 * sent as an Ioc limit order at the outcome price bound (see marketBoundPx).
 */
export type TicketTif = Tif | 'Market';

// Labels and help are getters so they follow the active language (#799). GTC / IOC / ALO keep their letters.
export const TIF_OPTIONS: readonly { value: TicketTif; readonly label: string; readonly help: string }[] = [
  {
    value: 'Gtc',
    label: 'GTC',
    get help() {
      return t('ticket.tif.gtc_help');
    },
  },
  {
    value: 'Ioc',
    label: 'IOC',
    get help() {
      return t('ticket.tif.ioc_help');
    },
  },
  {
    value: 'Alo',
    label: 'ALO',
    get help() {
      return t('ticket.tif.alo_help');
    },
  },
  {
    value: 'Market',
    get label() {
      return t('ticket.tif.market');
    },
    get help() {
      return t('ticket.tif.market_help');
    },
  },
];

/** #2242: the ticket's time-in-force buttons, left to right, and the one selected when a ticket opens. */
export const TICKET_TIF_ORDER: readonly TicketTif[] = ['Market', 'Ioc', 'Gtc', 'Alo'];
export const DEFAULT_TICKET_TIF: TicketTif = 'Market';

/**
 * #2242: what a click on a level of the book fills in (with the clicked price). An ask is lifted: buy, IOC. A bid with
 * shares of that side held is hit: sell, IOC (the ticket's sells only ever close held shares). A bid with nothing held to
 * sell joins the bid instead: buy, GTC, resting at that price.
 */
export function bookClickOrder(clickedAsk: boolean, holdsSide: boolean): { isBuy: boolean; tif: TicketTif } {
  if (clickedAsk) return { isBuy: true, tif: 'Ioc' };
  if (holdsSide) return { isBuy: false, tif: 'Ioc' };
  return { isBuy: true, tif: 'Gtc' };
}

/** The wire TIF for a ticket choice: Market is an Ioc order. */
export const wireTif = (t: TicketTif): Tif => (t === 'Market' ? 'Ioc' : t);

/**
 * Cents as typed ("63.2", "63.2¢", " 5 ") -> price string ("0.632"). Exact (no floats).
 * Returns null for empty / non-numeric input.
 */
export function centsToPx(input: string): string | null {
  const t = input.replace(/¢/g, '').replace(/c$/i, '').trim();
  if (t === '') return null;
  const d = parseDec(t);
  if (!d) return null;
  return decToString({ n: d.n, s: d.s + 2 });
}

/** Price string ("0.632") -> cents string for the input ("63.2"). */
export function pxToCentsInput(px: string): string {
  const d = parseDec(px);
  if (!d) return '';
  // x100: shift the scale by 2 (scale never negative: multiply n instead).
  return d.s >= 2 ? decToString({ n: d.n, s: d.s - 2 }) : decToString({ n: d.n * 10n ** BigInt(2 - d.s), s: 0 });
}

export interface TicketInput {
  side: Side;
  isBuy: boolean;
  /** Price string (0..1) or null when the cents box is empty / junk. */
  px: string | null;
  /** Shares as typed. */
  sz: string;
  holdings: OutcomeHoldings;
  /** Available USDC, or undefined while unknown (check skipped). */
  usdcAvailable?: string;
  sideLabels: readonly [string, string];
}

export interface TicketCheck {
  result: IntentResult;
  /** All blocking messages (price/size/holdings/buying power). Empty when ok. */
  errors: string[];
  warnings: string[];
}

export function checkTicket(t: TicketInput): TicketCheck {
  const errors: string[] = [];
  const result = validateOrderIntent({
    side: t.side,
    isBuy: t.isBuy,
    sz: t.sz.trim() === '' ? '' : t.sz.trim(),
    px: t.px ?? undefined,
    holdings: t.holdings,
    usdcAvailable: t.usdcAvailable,
    reduceOnly: !t.isBuy,
    sideLabels: t.sideLabels,
  });
  if (t.px === null) errors.push(tr('ticket.error.enter_price', { example: localizeNumber('63.5') }));
  if (!result.ok) errors.push(...result.reasons);
  return { result, errors, warnings: result.warnings };
}

export interface Economics {
  /** USDC paid (buy) or received (sell): px * sz. */
  notional: string;
  /** Buy only: sz * $1 if the side wins. */
  maxPayout?: string;
  /** Buy only: maxPayout - notional. (If the side loses it pays $0, so a buy risks exactly `notional`.) */
  profit?: string;
}

/**
 * Exact order economics, or null when px/sz are not usable numbers. Single source for the ticket and the /learn
 * calculator (#777): a share pays $1 if its side wins and $0 otherwise, so a buy risks exactly its cost.
 */
export function economics(px: string | null, sz: string, isBuy: boolean): Economics | null {
  if (px === null) return null;
  const p = parseDec(px);
  const s = parseDec(sz);
  if (!p || !s || cmp(p, ZERO) <= 0 || cmp(s, ZERO) <= 0) return null;
  const n = mul(p, s);
  if (!isBuy) return { notional: decToString(n) };
  const payout = mul(s, ONE);
  return { notional: decToString(n), maxPayout: decToString(payout), profit: decToString(sub(payout, n)) };
}

export interface OutcomeOrder {
  outcome: number;
  side: Side;
  isBuy: boolean;
  /** Price string in (0, 1). */
  px: string;
  /** Whole shares. */
  sz: string;
  tif: Tif;
  /**
   * Builder code (#770, builder/builder.ts): `{b, f}` appended to the action. Absent/null (builder disabled, the
   * default) = no `builder` key at all. Both signing paths (wallet and 1-click agent) sign this same action.
   */
  builder?: BuilderInfo | null;
}

/**
 * The exact `order` action: asset = outcomes.assetId (100_000_000 + 10*outcome + side), p/s from rules.wirePrice /
 * wireSize. `r` (reduce-only) is always false: Hyperliquid rejects reduce-only outcome orders with "Reduce-only is
 * invalid for spot trading." (#745, live testnet). Spec 2.5 (sells only close what is held) is enforced by the ticket
 * instead (checkTicket / validateOrderIntent: a sell can never exceed the shares held on that side).
 */
export function buildOutcomeOrderAction(o: OutcomeOrder): OrderAction {
  return orderAction(
    [
      {
        asset: assetId(o.outcome, o.side),
        isBuy: o.isBuy,
        px: wirePrice(o.px, OUTCOME_RULES),
        sz: wireSize(o.sz, OUTCOME_RULES),
        reduceOnly: false,
        tif: o.tif,
      },
    ],
    'na',
    o.builder,
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Synthetic Market orders (#746). Hyperliquid has no native market order; Market = an Ioc limit order at the most
// aggressive price an outcome allows, so it trades through every level the book has and never rests.
//
// Bounds: outcome prices lie strictly in (0, 1) with at most OUTCOME_PX_DECIMALS (5) decimals
// (docs/research/OUTCOME_IDS.md section 4, trading/rules.ts validatePrice), so the most aggressive valid prices are
//   buy  0.99999 (= 1 - 1e-5)   sell  0.00001 (= 1e-5).
// Confirmed read-only on testnet /info (2026-10-17, #746, docs/research/samples/s746-market-bounds-testnet.txt):
// historicalOrders of 85 active outcome users hold Hyperliquid's own UI market orders (tif "FrontendMarket") and
// API Ioc orders at exactly limitPx 0.99999 (buy) / 0.00001 (sell), accepted and filled; no other extreme price
// appears. No price band relative to mid was observed, unlike perps.

/** Most aggressive valid outcome price: buy `0.99999`, sell `0.00001`. */
export function marketBoundPx(isBuy: boolean): string {
  const tick: Dec = { n: 1n, s: OUTCOME_PX_DECIMALS };
  return decToString(isBuy ? sub(ONE, tick) : tick);
}

/** a / b rounded half-up to `decimals` places (both positive). */
function divDec(a: Dec, b: Dec, decimals: number): Dec {
  // a.n/10^a.s / (b.n/10^b.s) = a.n * 10^(b.s + decimals + 1) / (b.n * 10^a.s), then one rounding step.
  const num = a.n * 10n ** BigInt(b.s + decimals + 1);
  const den = b.n * 10n ** BigInt(a.s);
  return roundDec({ n: num / den, s: decimals + 1 }, decimals, 'nearest');
}

export interface MarketEstimate {
  /** Shares the visible book can fill within the price bound (<= requested). */
  fillableSz: string;
  /** Volume-weighted average price of the fillable shares (8 decimals max), or null when nothing can fill. */
  avgPx: string | null;
  /** Sum of px * sz over the fillable shares (USDC paid on a buy / received on a sell). */
  notional: string;
  /** Price of the last level the order would reach, or null when nothing can fill. */
  lastPx: string | null;
  /** True when the visible book covers the whole size. */
  complete: boolean;
}

/**
 * Walk the book of the side being traded to estimate a market order: a buy takes asks (levels[1], ascending), a sell
 * hits bids (levels[0], descending). Levels beyond the price bound (e.g. a "1.0" ask) can never match and are
 * skipped. Exact decimals. Null when `sz` is not a positive number. An estimate only: the book moves.
 */
export function estimateMarketFill(
  book: Pick<L2Book, 'levels'> | null | undefined,
  isBuy: boolean,
  sz: string,
): MarketEstimate | null {
  const want = parseDec(sz.trim());
  if (!want || cmp(want, ZERO) <= 0) return null;
  const bound = parseDec(marketBoundPx(isBuy)) as Dec;
  const levels = (isBuy ? book?.levels?.[1] : book?.levels?.[0]) ?? [];
  let left = want;
  let filled = ZERO;
  let notional = ZERO;
  let lastPx: string | null = null;
  for (const l of levels) {
    if (cmp(left, ZERO) <= 0) break;
    const p = parseDec(l.px);
    const s = parseDec(l.sz);
    if (!p || !s || cmp(s, ZERO) <= 0 || cmp(p, ZERO) <= 0) continue;
    if (isBuy ? cmp(p, bound) > 0 : cmp(p, bound) < 0) continue;
    const take = cmp(s, left) < 0 ? s : left;
    filled = add(filled, take);
    notional = add(notional, mul(p, take));
    left = sub(left, take);
    lastPx = decToString(p);
  }
  const any = cmp(filled, ZERO) > 0;
  return {
    fillableSz: decToString(filled),
    avgPx: any ? decToString(divDec(notional, filled, 8)) : null,
    notional: decToString(notional),
    lastPx,
    complete: cmp(left, ZERO) <= 0,
  };
}

export interface MarketTicketInput extends Omit<TicketInput, 'px'> {
  /** l2Book of the ticket's side (`coin(outcome, side)`), or null while it loads. */
  book: Pick<L2Book, 'levels'> | null | undefined;
}

export interface MarketTicketCheck extends TicketCheck {
  /** The price sent on the wire (marketBoundPx). */
  boundPx: string;
  /** Estimate from the current book, or null when the size is not a positive number. */
  estimate: MarketEstimate | null;
  /** Worst case: boundPx * sz (most a buy can pay, least a sell can receive), or null for a junk size. */
  worstNotional: string | null;
}

/**
 * Validate a Market ticket. Same rules as a limit ticket (whole shares, sells <= held, buy-only without a position),
 * evaluated at the bound price:
 *   - buying power: a buy must be covered at the WORST case (0.99999 * sz), because that is the limit price sent;
 *     Hyperliquid checks the balance against the order's limit price (testnet: FrontendMarket buys at 0.99999 were
 *     rejected with insufficientSpotBalanceRejected), and the book can move before the order lands;
 *   - min order value (#749): a WARNING, not the limit ticket's block, computed at the ESTIMATED fill
 *     (estimated avg price * sz), never at the bound. Hyperliquid does not measure a market order at its bound price:
 *     Ioc sells at 0.00001 worth 0.0001 USDC at that price were accepted and filled at the book (docs/testing/745 C2,
 *     10 of 20 shares held; testnet FrontendMarket sells of part of a position at 0.00001, see
 *     docs/research/samples/s749-min-value-testnet.txt). What it measures instead (the fill, a mark price, ...) is
 *     not observable read-only, and the estimate can be stale, so a block could refuse orders Hyperliquid accepts.
 *     A sell of the whole holding never warns (accepted below the minimum, as for limit orders). No book estimate:
 *     no min-value warning, but an empty-book warning instead;
 *   - extra soft warnings: nothing on the book to fill against / the visible book covers only part of the size.
 */
export function checkMarketTicket(t: MarketTicketInput): MarketTicketCheck {
  const boundPx = marketBoundPx(t.isBuy);
  const estimate = estimateMarketFill(t.book, t.isBuy, t.sz);
  const sz = t.sz.trim();
  const result = validateOrderIntent({
    side: t.side,
    isBuy: t.isBuy,
    sz,
    px: boundPx,
    holdings: t.holdings,
    usdcAvailable: t.usdcAvailable,
    reduceOnly: !t.isBuy,
    sideLabels: t.sideLabels,
    checkMinValue: false,
  });
  const errors = result.ok ? [] : [...result.reasons];
  const warnings = [...result.warnings];
  const label = t.sideLabels[t.side] ?? (t.side === 0 ? 'Yes' : 'No');
  const szOk = validateSize(sz, OUTCOME_RULES).ok;
  if (szOk && estimate?.avgPx) {
    const held = t.side === 0 ? t.holdings.holdYes : t.holdings.holdNo;
    if (t.isBuy || !sellsWholeHolding(sz, held)) {
      const w = marketMinValueWarning(estimate.avgPx, sz);
      if (w) warnings.push(w);
    }
  }
  if (estimate && szOk && t.book) {
    if (estimate.avgPx === null) {
      warnings.push(tr(t.isBuy ? 'ticket.warn.no_sellers' : 'ticket.warn.no_buyers', { side: label }));
    } else if (!estimate.complete) {
      warnings.push(
        tr('ticket.warn.partial', {
          fillable: localizeNumber(estimate.fillableSz),
          sz: localizeNumber(decToString(parseDec(sz) as Dec)),
        }),
      );
    }
  }
  const worst = szOk ? mul(parseDec(boundPx) as Dec, parseDec(sz) as Dec) : null;
  return {
    result: { ...result, warnings } as IntentResult,
    errors,
    warnings,
    boundPx,
    estimate,
    worstNotional: worst ? decToString(worst) : null,
  };
}

/**
 * Market min-order-value warning (#749): the estimated value avgPx * sz (rounded down to cents) below `minUsd`
 * (default 1 USDC). Null when it is big enough or the inputs are not positive numbers.
 */
export function marketMinValueWarning(avgPx: string, sz: string, minUsd: number = MIN_ORDER_VALUE_USD): string | null {
  const p = parseDec(avgPx);
  const s = parseDec(sz.trim());
  const min = parseDec(minUsd);
  if (!p || !s || !min || cmp(min, ZERO) <= 0) return null;
  const n = mul(p, s);
  if (cmp(n, ZERO) <= 0 || cmp(n, min) >= 0) return null;
  return t('ticket.warn.market_min', { value: usd(decToString(roundDec(n, 2, 'down'))), min: localizeNumber(decToString(min)) });
}

/** The `order` action for a Market ticket: an Ioc order at the price bound (wire strings via rules.ts as usual). */
export function buildMarketOrderAction(o: Omit<OutcomeOrder, 'px' | 'tif'>): OrderAction {
  return buildOutcomeOrderAction({ ...o, px: marketBoundPx(o.isBuy), tif: 'Ioc' });
}

/** Monotonic nonce: Date.now(), but always strictly greater than the previous one in this session. */
export function createNonceSource(now: () => number = Date.now): () => number {
  let last = 0;
  return () => {
    const n = Math.max(now(), last + 1);
    last = n;
    return n;
  };
}

/** App-wide nonce source for the session. */
export const nextNonce = createNonceSource();

export type OrderStatusResult =
  | { kind: 'resting'; oid: number }
  | { kind: 'filled'; oid?: number; totalSz: string; avgPx: string }
  | { kind: 'error'; message: string };

export type SubmitResult =
  | { kind: 'ok'; statuses: OrderStatusResult[] }
  | { kind: 'err'; message: string }
  | { kind: 'rejected'; hint?: string };

/** Parse a POST /exchange response for an `order` action. Never throws. */
export function parseOrderResponse(resp: ExchangeResponse | unknown): SubmitResult {
  if (!resp || typeof resp !== 'object') return { kind: 'err', message: `Unexpected response: ${JSON.stringify(resp)}` };
  const r = resp as { status?: unknown; response?: unknown };
  if (r.status !== 'ok') {
    const msg = typeof r.response === 'string' ? r.response : JSON.stringify(r.response ?? resp);
    return { kind: 'err', message: msg };
  }
  const data = (r.response as { data?: { statuses?: unknown } } | undefined)?.data;
  const raw = Array.isArray(data?.statuses) ? (data.statuses as unknown[]) : null;
  if (!raw) return { kind: 'err', message: `Unexpected response: ${JSON.stringify(resp)}` };
  const statuses = raw.map((s): OrderStatusResult => {
    if (s && typeof s === 'object') {
      const o = s as {
        resting?: { oid?: unknown };
        filled?: { totalSz?: unknown; avgPx?: unknown; oid?: unknown };
        error?: unknown;
      };
      if (o.resting && typeof o.resting.oid === 'number') return { kind: 'resting', oid: o.resting.oid };
      if (o.filled) {
        return {
          kind: 'filled',
          oid: typeof o.filled.oid === 'number' ? o.filled.oid : undefined,
          totalSz: String(o.filled.totalSz ?? ''),
          avgPx: String(o.filled.avgPx ?? ''),
        };
      }
      if (typeof o.error === 'string') return { kind: 'error', message: o.error };
    }
    if (typeof s === 'string') return { kind: 'error', message: s };
    return { kind: 'error', message: `Unexpected status: ${JSON.stringify(s)}` };
  });
  return { kind: 'ok', statuses };
}

/** EIP-1193 4001 (user rejected), also when wrapped (MetaMask mobile) or only in the message. See wallet/errors.ts. */
export { isUserRejection };

/** An approved agent wallet (#688) that signs L1 actions locally. The key stays inside `sign`. */
export interface AgentSigner {
  /** Lowercase user address the agent was approved for (must be the account submitting). */
  user: string;
  agentAddress: Hex;
  sign(action: unknown, nonce: number): Promise<ExchangePayload>;
}

export interface SubmitDeps {
  provider: Eip1193Provider;
  account: Hex;
  env: Pick<EnvConfig, 'exchangeUrl' | 'signingSource'>;
  nonce?: () => number;
  fetchImpl?: typeof fetch;
  /** EIP-6963 rdns of the connected wallet: picks its chain-1337 handling (wallet/compat.ts, #687). */
  walletRdns?: string | null;
  /**
   * 1-click trading (#688): sign with this agent instead of the wallet. If Hyperliquid answers that the agent does
   * not exist (replaced / expired / pruned), `onAgentMissing` runs (the caller forgets the agent) and the action is
   * re-signed with the wallet, with a fresh nonce.
   */
  agent?: AgentSigner | null;
  onAgentMissing?: (message: string) => void;
}

export type PostResult =
  | { kind: 'ok'; resp: ExchangeResponse }
  | { kind: 'err'; message: string }
  /** `hint`: wallet-specific explanation when the rejection was of adding the signing network (e.g. Rabby). */
  | { kind: 'rejected'; hint?: string };

/**
 * Shared by orders and cancels: switch the wallet to chain 1337, take the nonce (after the switch, just before
 * signing), sign the L1 action and POST it. A wallet rejection resolves to {kind:'rejected'}; other wallet /
 * network failures resolve to {kind:'err'}. Never throws. The sockets stay open meanwhile, however long the tab is hidden
 * (tabSleep.ts): on a phone the wallet's prompt itself hides the tab, and the order's events need a socket to arrive on.
 */
export async function signAndPost(action: unknown, deps: SubmitDeps): Promise<PostResult> {
  const release = holdSocketsOpen();
  try {
    return await signAndPostHeld(action, deps);
  } finally {
    release();
  }
}

async function signAndPostHeld(action: unknown, deps: SubmitDeps): Promise<PostResult> {
  const agent = deps.agent;
  if (agent && agent.user === deps.account.toLowerCase()) {
    // Defence in depth for the mainnet gate (#684): the UI already applies submitGate() before getting here.
    if ((deps.env as Partial<EnvConfig>).isMainnet && !mainnetTradingEnabled()) {
      return { kind: 'err', message: t('common.mainnet_off') };
    }
    let payload: ExchangePayload;
    try {
      payload = await agent.sign(action, (deps.nonce ?? nextNonce)());
    } catch (e) {
      return { kind: 'err', message: t('ticket.err.agent_sign', { error: e instanceof Error ? e.message : String(e) }) };
    }
    let resp: ExchangeResponse;
    try {
      resp = await postExchange(deps.env, payload, deps.fetchImpl);
    } catch (e) {
      return { kind: 'err', message: e instanceof Error ? e.message : String(e) };
    }
    const msg = resp.status !== 'ok' && typeof resp.response === 'string' ? resp.response : null;
    if (msg === null || !isAgentMissingError(msg)) return { kind: 'ok', resp };
    // The agent is gone on Hyperliquid: forget it and fall back to wallet signing.
    deps.onAgentMissing?.(msg);
  }
  return walletSignAndPost(action, deps);
}

async function walletSignAndPost(action: unknown, deps: SubmitDeps): Promise<PostResult> {
  let payload;
  try {
    // The nonce is taken after any chain switch, just before the signature request.
    payload = await signL1(deps.provider, deps.account, action, deps.nonce ?? nextNonce, deps.env, {
      walletRdns: deps.walletRdns,
    });
  } catch (e) {
    if (isUserRejection(e)) {
      const hint = e instanceof ChainAddError ? walletProfile(deps.walletRdns ?? null, deps.provider as never).addChainHint : undefined;
      return hint ? { kind: 'rejected', hint } : { kind: 'rejected' };
    }
    return { kind: 'err', message: t('wallet.error_generic', { error: walletErrorMessage(e) }) };
  }
  try {
    return { kind: 'ok', resp: await postExchange(deps.env, payload, deps.fetchImpl) };
  } catch (e) {
    return { kind: 'err', message: e instanceof Error ? e.message : String(e) };
  }
}

/** Sign + submit an `order` action and parse the per-order statuses. */
export async function signAndSubmitOrder(action: OrderAction, deps: SubmitDeps): Promise<SubmitResult> {
  const r = await signAndPost(action, deps);
  return r.kind === 'ok' ? parseOrderResponse(r.resp) : r;
}

// ---------------------------------------------------------------------------------------------------------------
// Mainnet: one extra confirmation on the first order of the browser session.

const MAINNET_CONFIRM_KEY = 'hip4.ticket.mainnetConfirmed';
let mainnetConfirmed = false;

export function isMainnetConfirmed(): boolean {
  if (mainnetConfirmed) return true;
  try {
    return window.sessionStorage.getItem(MAINNET_CONFIRM_KEY) === '1';
  } catch {
    return false;
  }
}

export function markMainnetConfirmed(): void {
  mainnetConfirmed = true;
  try {
    window.sessionStorage.setItem(MAINNET_CONFIRM_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** Test helper. */
export function resetMainnetConfirmed(): void {
  mainnetConfirmed = false;
  try {
    window.sessionStorage.removeItem(MAINNET_CONFIRM_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * What submitting the ticket does on this network (shared/fixtures/rules.json `submitGate`):
 *   'mainnet-off'      mainnet and mainnet trading is not enabled: no order can be sent;
 *   'mainnet-confirm'  first mainnet order of the session: ask for one extra confirmation first;
 *   'ok'               sign and send (testnet always, mainnet once confirmed).
 */
export type SubmitGate = 'ok' | 'mainnet-off' | 'mainnet-confirm';

export function submitGate(isMainnet: boolean, mainnetTradingOn: boolean, mainnetConfirmed: boolean): SubmitGate {
  if (!isMainnet) return 'ok';
  if (!mainnetTradingOn) return 'mainnet-off';
  return mainnetConfirmed ? 'ok' : 'mainnet-confirm';
}

/** Mainnet order submission stays off until testnet sign-off (#684); enable with VITE_MAINNET_TRADING=1 at build. */
export const mainnetTradingEnabled = (): boolean => import.meta.env.VITE_MAINNET_TRADING === '1';
